/**
 * Opportunities seller desk: the queue, one opportunity (requirements, the buyer's choice and counter, quotation builder, revision by
 * discount or target price, accept the buyer price, manager / head of operations approval, send, close as lost) and the sales staff page.
 * Used by Quixo's staff console and by an agency's own desk in the partner portal; the surface supplies the environment (env.tsx).
 * Every price is computed by the API; the preview here uses the shared contract helpers.
 */
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { Badge, Button, Dialog, Field, IconChevron, Input, KitProvider, Select, Textarea } from '@qm/ui/kit';
import { formatDateTime } from '@qm/i18n';
import {
  OPPORTUNITY_STATUSES, discountFor, discounted, selectionTotal,
  type BuyerKind, type OpportunityStatus, type OpportunityView, type QuotationItemInput, type SellerView, type VersionView,
} from '@qm/contracts/opportunities';
import { useCopy, useOpp, type ApiPath } from './env';
import { STATUS_TONE, fmtMoney as money, pct, toMinor } from './format';
import { Activity, BackLink, ChipSelect, DialogActions, Empty, Fact, Facts, IconPlus, SearchField, Section, ViewTabs } from './parts';
import './opportunities.css';

type Summary = { id: string; number: string; title: string; status: OpportunityStatus; currency: string; tenantId: string; buyerKind: BuyerKind; seller: SellerView; accountManagerId: string | null; customerRequiredPriceMinor: number | null; finalTotalMinor: number | null; updatedAt: string; buyerName?: string };
type StaffProfile = { userId: string; name: string; email: string; managerId: string | null; headOfOperations: boolean; selfApprovalBps: number; active: boolean };
type Me = { userId: string; canSell: boolean; canManageStaff: boolean; canAssign: boolean; seller: SellerView; profile: StaffProfile | null };

/** Desk API: `<apiBase><p>` (apiBase is `/v1/opportunities/admin` or `/v1/opportunities/agency`). */
function useDesk() {
  const env = useOpp();
  const path = (p: string) => `${env.apiBase}${p}` as ApiPath;
  return {
    ...env,
    path,
    get: <T,>(p: string) => env.http.get<T>(path(p)),
    post: <T,>(p: string, body?: unknown) => env.http.send<T>('POST', path(p), body),
    put: <T,>(p: string, body?: unknown) => env.http.send<T>('PUT', path(p), body),
  };
}

function useErr() {
  const { t, failure } = useOpp();
  return (e: unknown) => {
    const f = failure(e);
    if (!f.status) return t('opportunities.error.generic');
    if (f.reason) { const k = `opportunities.error.reason.${f.reason}`; const v = t(k); if (v !== k) return v; }
    if (f.status === 404) return t('opportunities.error.notFound');
    if (f.status === 403) return t('opportunities.error.forbidden');
    if (f.status === 409) return f.message ?? t('opportunities.error.changed');
    return f.message ?? t('opportunities.error.generic');
  };
}

// ====================================================================== queue
export function DeskQueueScreen() {
  const { t, locale, links, Screen, get } = useDesk();
  const copy = useCopy();
  const [view, setView] = useState<'all' | 'mine' | 'approvals'>('all');
  const [status, setStatus] = useState<OpportunityStatus | ''>('');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<Summary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const err = useErr();
  useEffect(() => {
    setRows(null); setError(null);
    const q = new URLSearchParams({ view, ...(status ? { status } : {}) });
    get<Summary[]>(`/opportunities?${q.toString()}`).then(setRows, (e: unknown) => setError(err(e)));
  }, [view, status]);
  const shown = useMemo(() => {
    const n = query.trim().toLowerCase();
    return (rows ?? []).filter((r) => !n || [r.number, r.title, r.buyerName, r.tenantId].some((x) => x?.toLowerCase().includes(n)));
  }, [rows, query]);

  return (
    <KitProvider locale={locale}>
      <Screen title={copy.text('title')} subtitle={copy.text('intro')} testId="opportunities-queue"
        actions={<Link className="qk-btn" data-variant="secondary" data-size="md" to={links.staff}><span className="qk-btn__label">{t('opportunities.admin.staffLink')}</span></Link>}>
        <div className="qop">
          <ViewTabs label={t('opportunities.admin.view')} value={view} onChange={setView}
            options={(['all', 'mine', 'approvals'] as const).map((v) => ({ id: v, label: t(`opportunities.admin.view.${v}`) }))} />
          <div className="qop-toolbar">
            <SearchField label={t('opportunities.list.search')} value={query} onChange={setQuery} />
            <ChipSelect label={t('opportunities.admin.status')} value={status} onChange={(v) => setStatus(v as OpportunityStatus | '')}>
              <option value="">{t('opportunities.admin.anyStatus')}</option>
              {OPPORTUNITY_STATUSES.filter((s) => s !== 'draft').map((s) => <option key={s} value={s}>{t(`opportunities.status.${s}`)}</option>)}
            </ChipSelect>
            {rows ? <span className="qop-count" role="status">{t('opportunities.list.count', { count: String(shown.length) })}</span> : null}
          </div>
          {error ? <p role="alert" className="qop-error">{error}</p> : null}
          {rows && rows.length === 0 ? <Empty text={t('opportunities.admin.empty')} /> : null}
          {rows && rows.length > 0 && shown.length === 0 ? <Empty text={t('opportunities.list.noMatch')} /> : null}
          {shown.length ? (
            <div className="qop-list">
              <div className="qop-rowhead" aria-hidden="true">
                <span className="qop-row__num">{t('opportunities.admin.col.number')}</span><span className="qop-row__title">{t('opportunities.admin.col.title')}</span><span className="qop-row__status">{t('opportunities.admin.col.status')}</span>
                <span className="qop-row__buyer">{t('opportunities.admin.col.buyer')}</span><span className="qop-row__price">{t('opportunities.admin.col.price')}</span><span className="qop-row__upd">{t('opportunities.admin.col.updated')}</span>
              </div>
              <ul className="qop-rows">
                {shown.map((r) => (
                  <li key={r.id}>
                    <Link to={links.detail(r.id)} className="qop-row">
                      <span className="qop-row__meta">
                        <span className="qop-row__num" dir="ltr">{r.number}</span>
                        <span className="qop-row__buyer"><bdi>{r.buyerName ?? r.tenantId}</bdi>{r.buyerKind === 'partner' ? <> <Badge tone="neutral">{t('opportunities.buyerKind.partner')}</Badge></> : null}</span>
                      </span>
                      <span className="qop-row__title">{r.title}</span>
                      <span className="qop-row__status"><Badge tone={STATUS_TONE[r.status]}>{t(`opportunities.status.${r.status}`)}</Badge></span>
                      <span className="qop-row__price" dir="ltr">{r.finalTotalMinor !== null ? money(locale, r.currency, r.finalTotalMinor) : r.customerRequiredPriceMinor !== null && r.status === 'negotiating' ? `${t('opportunities.admin.asks')} ${money(locale, r.currency, r.customerRequiredPriceMinor)}` : '—'}</span>
                      <span className="qop-row__upd">{formatDateTime(locale, r.updatedAt, { style: 'date' })}</span>
                      <span className="qop-row__go" aria-hidden="true"><IconChevron width={18} height={18} /></span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </Screen>
    </KitProvider>
  );
}

// ====================================================================== detail
type Dlg = null | 'assign' | 'approve' | 'reject' | 'revise' | 'quote' | 'close';

export function DeskDetailScreen() {
  const { t, locale, roles, links, Screen, can, get, post } = useDesk();
  const copy = useCopy();
  const { id = '' } = useParams();
  const [opp, setOpp] = useState<OpportunityView | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [staff, setStaff] = useState<StaffProfile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dlg, setDlg] = useState<Dlg>(null);
  const err = useErr();
  const base = `/opportunities/${encodeURIComponent(id)}`;

  useEffect(() => {
    get<OpportunityView>(base).then(setOpp, (e: unknown) => setError(err(e)));
    get<Me>('/me').then(setMe, () => undefined);
    get<StaffProfile[]>('/staff').then(setStaff, () => undefined);
  }, [base]);

  /** Runs a change; resolves true when it worked (a dialog then closes), false after showing the error. */
  const act = async (fn: () => Promise<OpportunityView>, ok: string): Promise<boolean> => {
    setBusy(true); setError(null); setNotice(null);
    try { setOpp(await fn()); setNotice(t(ok)); return true; } catch (e) { setError(err(e)); return false; } finally { setBusy(false); }
  };
  const submitDlg = async (fn: () => Promise<OpportunityView>, ok: string) => { if (await act(fn, ok)) setDlg(null); };
  const closeDlg = () => { setDlg(null); setError(null); };

  if (!opp) return <KitProvider locale={locale}><Screen title={copy.text('title')}>{error ? <p role="alert" className="qop-error">{error}</p> : <p>{t('common.loading')}</p>}</Screen></KitProvider>;

  const r = opp.requirements;
  const name = (uid: string | null) => (uid ? staff.find((s) => s.userId === uid)?.name ?? uid : t('opportunities.admin.unassigned'));
  const sent = [...opp.versions].reverse().find((v) => v.sentAt);
  const pending = opp.versions.find((v) => v.status === 'pending_approval');
  const currentStep = pending?.approvals?.find((a) => a.decision === 'pending');
  const mine = !!me && (opp.accountManagerId === me.userId || (!opp.accountManagerId && !!me.profile) || can.actAs(roles));
  const sell = can.sell(roles) && mine;
  const open = !['accepted', 'rejected'].includes(opp.status);
  const counter = opp.status === 'negotiating' && opp.customerRequiredPriceMinor !== null && sent;
  const iApprove = !!pending && !!currentStep && !!me && currentStep.approverId === me.userId;
  const canRevise = sell && !!sent && ['quoted', 'negotiating', 'approved'].includes(opp.status);
  const canQuote = sell && ['submitted', 'quoted', 'negotiating', 'approved'].includes(opp.status);
  const canClose = can.sell(roles) && open;
  const canAssign = can.assign(roles) && open && opp.status !== 'pending_approval';
  const dlgError = dlg && error ? error : null;

  let primary: ReactNode = null;
  if (sell && counter) primary = <Button variant="accent" loading={busy} onClick={() => void act(() => post(`${base}/accept-counter`), 'opportunities.admin.done.counterAccepted')}>{t('opportunities.admin.acceptCounter')}</Button>;
  else if (sell && opp.status === 'approved') primary = <Button variant="accent" loading={busy} onClick={() => void act(() => post(`${base}/send`), 'opportunities.admin.done.sent')}>{t('opportunities.admin.send')}</Button>;
  else if (canQuote && !sent) primary = <Button variant="accent" onClick={() => setDlg('quote')}>{t('opportunities.admin.createQuotation')}</Button>;

  return (
    <KitProvider locale={locale}>
      <Screen eyebrow={opp.number} title={r.title} subtitle={t(`opportunities.admin.statusHelp.${opp.status}`)} testId="opportunity-admin"
        actions={<><Badge tone={STATUS_TONE[opp.status]}>{t(`opportunities.status.${opp.status}`)}</Badge>{primary}</>}>
        <div className="qop">
          <BackLink to={links.list}>{t('opportunities.back')}</BackLink>
          {error && !dlg ? <p role="alert" className="qop-error">{error}</p> : null}
          {notice ? <p role="status" className="qop-msg">{notice}</p> : null}
          {canRevise || (canQuote && sent) ? (
            <div className="qop-actionbar">
              {canRevise ? <Button variant="secondary" onClick={() => setDlg('revise')}>{t('opportunities.admin.revise')}</Button> : null}
              {canQuote && sent ? <Button variant="secondary" onClick={() => setDlg('quote')}>{t('opportunities.admin.newQuotation')}</Button> : null}
            </div>
          ) : null}

          <div className="qop-detail">
            <div className="qop-main">
              {pending && currentStep ? (
                <section className="qop-banner" aria-labelledby="opp-approval">
                  <h2 id="opp-approval">{t('opportunities.admin.approval')}</h2>
                  <p>{t('opportunities.admin.approvalSummary', { version: pending.version, price: money(locale, opp.currency, pending.offeredTotalMinor), discount: pct(pending.discountBps) })}</p>
                  <ol className="qop-steps">
                    {pending.approvals!.map((a) => (
                      <li key={a.stage}>
                        <span>{t(`opportunities.stage.${a.stage}`)}: {name(a.approverId)}{a.note ? <span className="qop-muted"> · {a.note}</span> : null}</span>
                        <Badge tone={a.decision === 'approved' ? 'success' : a.decision === 'rejected' ? 'danger' : 'warning'}>{t(`opportunities.decision.${a.decision}`)}</Badge>
                      </li>
                    ))}
                  </ol>
                  {iApprove ? (
                    <div className="qop-banner__actions">
                      <Button variant="secondary" onClick={() => setDlg('reject')}>{t('opportunities.admin.rejectPrice')}</Button>
                      <Button variant="accent" onClick={() => setDlg('approve')}>{t('opportunities.admin.approve')}</Button>
                    </div>
                  ) : <p className="qop-muted">{t('opportunities.admin.waitingFor', { name: name(currentStep.approverId) })}</p>}
                </section>
              ) : null}

              {counter && sent ? (
                <section className="qop-banner" aria-labelledby="opp-counter">
                  <h2 id="opp-counter">{t('opportunities.admin.counter')}</h2>
                  <dl className="qop-dl">
                    <div><dt>{t('opportunities.admin.selected')}</dt><dd>{sent.items.filter((i) => opp.selection.includes(i.id)).map((i) => i.name).join(', ')}</dd></div>
                    <div><dt>{t('opportunities.admin.quotedForSelection')}</dt><dd dir="ltr">{money(locale, opp.currency, selectionTotal(sent.items, opp.selection))} → {money(locale, opp.currency, discounted(selectionTotal(sent.items, opp.selection), sent.discountBps))}</dd></div>
                    <div><dt>{t('opportunities.admin.required')}</dt><dd dir="ltr"><strong>{money(locale, opp.currency, opp.customerRequiredPriceMinor)}</strong> ({pct(discountFor(selectionTotal(sent.items, opp.selection), opp.customerRequiredPriceMinor!))} {t('opportunities.admin.offList')})</dd></div>
                    {opp.customerBudgetMinor !== null ? <div><dt>{t('opportunities.admin.budget')}</dt><dd dir="ltr">{money(locale, opp.currency, opp.customerBudgetMinor)}</dd></div> : null}
                    {opp.customerNote ? <div><dt>{t('opportunities.note')}</dt><dd className="qop-pre">{opp.customerNote}</dd></div> : null}
                  </dl>
                </section>
              ) : null}

              {opp.status === 'approved' && sell ? <p className="qop-banner qop-banner--note">{t('opportunities.admin.readyToSend')}</p> : null}

              <Section id="opp-versions" title={t('opportunities.admin.versions')}>
                {opp.versions.length === 0 ? <p className="qop-muted">{t('opportunities.admin.noVersions')}</p> : (
                  <div className="qop-scroll">
                    <table className="qop-table">
                      <thead><tr><th>#</th><th>{t('opportunities.admin.col.status')}</th><th>{t('opportunities.listTotal')}</th><th>{t('opportunities.discount')}</th><th>{t('opportunities.admin.offered')}</th><th>{t('opportunities.admin.col.updated')}</th></tr></thead>
                      <tbody>
                        {[...opp.versions].reverse().map((v) => (
                          <tr key={v.id}>
                            <td>{v.version}{v.acceptsCounter ? ` · ${t('opportunities.admin.counterPrice')}` : ''}</td>
                            <td>{t(`opportunities.version.${v.status}`)}</td>
                            <td dir="ltr">{money(locale, opp.currency, v.listTotalMinor)}</td>
                            <td dir="ltr">{pct(v.discountBps)}</td>
                            <td dir="ltr">{money(locale, opp.currency, v.offeredTotalMinor)}</td>
                            <td>{formatDateTime(locale, v.sentAt ?? v.createdAt, { style: 'date' })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>

              {sent ? (
                <Section id="opp-items" title={t('opportunities.items')}>
                  <ul className="qop-lines">
                    {sent.items.map((i) => (
                      <li key={i.id}>
                        <span className="qop-pickrow__text"><span className="qop-pickrow__name">{i.name}</span><span className="qop-pickrow__meta">{t(`opportunities.kind.${i.kind}`)}{i.required ? ` · ${t('opportunities.required')}` : ''}{i.description ? ` · ${i.description}` : ''}</span></span>
                        <span className="qop-price" dir="ltr">{money(locale, opp.currency, i.listPriceMinor)}</span>
                      </li>
                    ))}
                  </ul>
                </Section>
              ) : null}

              {opp.status === 'accepted' ? <p className="qop-final">{t('opportunities.finalPrice', { price: money(locale, opp.currency, opp.finalTotalMinor) })}</p> : null}
              {opp.closeReason ? <p className="qop-muted">{t('opportunities.closeReason', { reason: opp.closeReason })}</p> : null}

              <Section id="opp-req" title={t('opportunities.requirements')}>
                <dl className="qop-dl">
                  {r.services.length ? <div><dt>{t('opportunities.form.services')}</dt><dd>{r.services.map((s) => t(`opportunities.service.${s}`)).join(', ')}</dd></div> : null}
                  {r.modules.length ? <div><dt>{t('opportunities.form.modules')}</dt><dd>{r.modules.map((m) => t(`opportunities.module.${m}`)).join(', ')}</dd></div> : null}
                  {r.description ? <div><dt>{t('opportunities.form.description')}</dt><dd className="qop-pre">{r.description}</dd></div> : null}
                  {r.goals ? <div><dt>{t('opportunities.form.goals')}</dt><dd className="qop-pre">{r.goals}</dd></div> : null}
                  {r.timeline ? <div><dt>{t('opportunities.form.timeline')}</dt><dd>{t(`opportunities.timeline.${r.timeline}`)}</dd></div> : null}
                </dl>
              </Section>

              <Section id="opp-timeline" title={t('opportunities.timeline')}>
                <Activity opp={opp} actor={(e) => (e.actorType === 'staff' ? name(e.actorId) : t(`opportunities.actor.${e.actorType}`))} />
              </Section>
            </div>

            <aside className="qop-side" aria-label={t('opportunities.facts')}>
              <h2 className="qop-side__title">{t('opportunities.facts')}</h2>
              <Facts>
                <Fact label={t('opportunities.admin.col.buyer')}><bdi>{opp.buyerName ?? opp.tenantId}</bdi> · {t(`opportunities.buyerKind.${opp.buyerKind}`)}</Fact>
                <Fact label={t('opportunities.admin.accountManager')}>
                  {name(opp.accountManagerId)}
                  {canAssign ? <> <Button variant="ghost" size="sm" onClick={() => setDlg('assign')}>{t('opportunities.admin.assign')}</Button></> : null}
                </Fact>
                {r.budgetMinor !== undefined ? <Fact label={t('opportunities.form.budget')} dir="ltr">{money(locale, r.currency, r.budgetMinor)}</Fact> : null}
                {opp.customerRequiredPriceMinor !== null ? <Fact label={t('opportunities.admin.required')} dir="ltr">{money(locale, opp.currency, opp.customerRequiredPriceMinor)}</Fact> : null}
                {sent?.validUntil ? <Fact label={t('opportunities.admin.validUntil')}>{formatDateTime(locale, sent.validUntil, { style: 'date' })}</Fact> : null}
                {r.contactName || r.contactPhone ? <Fact label={t('opportunities.admin.contact')} dir="ltr">{[r.contactName, r.contactPhone].filter(Boolean).join(' · ')}</Fact> : null}
              </Facts>
              {canClose ? <Button variant="ghost" className="qop-danger qop-side__close" onClick={() => setDlg('close')}>{t('opportunities.admin.close')}</Button> : null}
            </aside>
          </div>
        </div>

        <Dialog open={dlg === 'assign'} onClose={closeDlg} title={t('opportunities.admin.accountManager')}>
          <AssignForm staff={staff} current={opp.accountManagerId} busy={busy} error={dlgError} onCancel={closeDlg}
            onAssign={(uid) => submitDlg(() => post(`${base}/assign`, { accountManagerId: uid }), 'opportunities.admin.done.assigned')} />
        </Dialog>
        {pending ? (
          <Dialog open={dlg === 'approve' || dlg === 'reject'} onClose={closeDlg} title={dlg === 'reject' ? t('opportunities.admin.rejectPrice') : t('opportunities.admin.approve')}>
            <DecisionForm reject={dlg === 'reject'} busy={busy} error={dlgError} onCancel={closeDlg}
              onDecide={(decision, note) => submitDlg(() => post(`${base}/versions/${encodeURIComponent(pending.id)}/decision`, { decision, note }), `opportunities.admin.done.${decision}`)} />
          </Dialog>
        ) : null}
        <Dialog open={dlg === 'revise'} onClose={closeDlg} size="lg" title={t('opportunities.admin.revise')} description={sent ? t('opportunities.admin.reviseHelp', { list: money(locale, opp.currency, selectionTotal(sent.items, opp.selection.length ? opp.selection : sent.basisItemIds)) }) : undefined}>
          {sent ? <ReviseForm opp={opp} version={sent} me={me} busy={busy} error={dlgError} onCancel={closeDlg} onRevise={(b) => submitDlg(() => post(`${base}/revise`, b), 'opportunities.admin.done.revised')} /> : null}
        </Dialog>
        <Dialog open={dlg === 'quote'} onClose={closeDlg} size="lg" title={sent ? t('opportunities.admin.newQuotation') : t('opportunities.admin.quotation')}>
          <QuotationForm opp={opp} from={sent} me={me} busy={busy} error={dlgError} onCancel={closeDlg} onSend={(b) => submitDlg(() => post(`${base}/quotation`, b), 'opportunities.admin.done.quoted')} />
        </Dialog>
        <Dialog open={dlg === 'close'} onClose={closeDlg} title={t('opportunities.admin.close')}>
          <CloseForm busy={busy} error={dlgError} onCancel={closeDlg} onClose={(reason) => submitDlg(() => post(`${base}/close`, { reason }), 'opportunities.admin.done.closed')} />
        </Dialog>
      </Screen>
    </KitProvider>
  );
}

function FormError({ error }: { error: string | null }) { return error ? <p role="alert" className="qop-error">{error}</p> : null; }

function AssignForm({ staff, current, busy, error, onAssign, onCancel }: { staff: StaffProfile[]; current: string | null; busy: boolean; error: string | null; onAssign: (uid: string) => Promise<void>; onCancel: () => void }) {
  const { t } = useOpp();
  const [uid, setUid] = useState(current ?? '');
  const active = staff.filter((s) => s.active);
  return (
    <form className="qop-dform" onSubmit={(e) => { e.preventDefault(); if (uid && uid !== current) void onAssign(uid); }}>
      <Field label={t('opportunities.admin.assignTo')}>{(p) => (
        <Select id={p.id} value={uid} onChange={(e) => setUid(e.target.value)}>
          <option value="">{t('opportunities.form.choose')}</option>
          {active.map((s) => <option key={s.userId} value={s.userId}>{s.name}</option>)}
        </Select>
      )}</Field>
      <FormError error={error} />
      <DialogActions cancelLabel={t('opportunities.cancel')} onCancel={onCancel} submitLabel={t('opportunities.admin.assign')} busy={busy} disabled={!uid || uid === current} />
    </form>
  );
}

function DecisionForm({ reject, busy, error, onDecide, onCancel }: { reject: boolean; busy: boolean; error: string | null; onDecide: (d: 'approve' | 'reject', note: string) => Promise<void>; onCancel: () => void }) {
  const { t } = useOpp();
  const [note, setNote] = useState('');
  const trimmed = note.trim();
  return (
    <form className="qop-dform" onSubmit={(e) => { e.preventDefault(); if (!reject || trimmed.length >= 3) void onDecide(reject ? 'reject' : 'approve', trimmed); }}>
      <Field label={t('opportunities.admin.decisionNote')} required={reject}>{(p) => <Textarea id={p.id} rows={3} maxLength={1000} required={reject} value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
      <FormError error={error} />
      <DialogActions cancelLabel={t('opportunities.cancel')} onCancel={onCancel} busy={busy} danger={reject} disabled={reject && trimmed.length < 3}
        submitLabel={reject ? t('opportunities.admin.rejectPrice') : t('opportunities.admin.approve')} />
    </form>
  );
}

function ApprovalHint({ discountBps, me }: { discountBps: number; me: Me | null }) {
  const { t } = useOpp();
  const limit = me?.profile?.selfApprovalBps;
  if (limit === undefined) return null;
  return <p className="qop-muted" role="note">{discountBps > limit ? t('opportunities.admin.needsApproval', { discount: pct(discountBps), limit: pct(limit) }) : t('opportunities.admin.withinLimit', { discount: pct(discountBps), limit: pct(limit) })}</p>;
}

function ReviseForm({ opp, version, me, busy, error, onRevise, onCancel }: { opp: OpportunityView; version: VersionView; me: Me | null; busy: boolean; error: string | null; onRevise: (b: Record<string, unknown>) => Promise<void>; onCancel: () => void }) {
  const { t, locale } = useOpp();
  const basis = opp.selection.length ? opp.selection : version.basisItemIds;
  const list = selectionTotal(version.items, basis);
  const [mode, setMode] = useState<'price' | 'percent'>('price');
  const [value, setValue] = useState('');
  const [message, setMessage] = useState('');
  const bps = mode === 'percent' ? Math.round(Number(value) * 100) : (toMinor(value) !== null ? discountFor(list, toMinor(value)!) : NaN);
  const valid = value.trim() !== '' && Number.isFinite(bps) && bps >= 0 && bps <= 10_000 && (mode === 'percent' || (toMinor(value) ?? Infinity) <= list);
  const offered = mode === 'percent' ? discounted(list, bps) : toMinor(value);
  const label = mode === 'percent' ? t('opportunities.admin.byPercent') : t('opportunities.admin.byPrice', { currency: opp.currency });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    void onRevise({ ...(mode === 'percent' ? { discountBps: bps } : { offeredTotalMinor: toMinor(value) }), basisItemIds: basis, message });
  };
  return (
    <form className="qop-dform" onSubmit={submit}>
      <div className="qop-radios" role="radiogroup" aria-label={t('opportunities.admin.reviseBy')}>
        <label><input type="radio" checked={mode === 'price'} onChange={() => { setMode('price'); setValue(''); }} /> {t('opportunities.admin.byPrice', { currency: opp.currency })}</label>
        <label><input type="radio" checked={mode === 'percent'} onChange={() => { setMode('percent'); setValue(''); }} /> {t('opportunities.admin.byPercent')}</label>
      </div>
      <Field label={label}>{(p) => <Input id={p.id} inputMode="decimal" dir="ltr" value={value} onChange={(e) => setValue(e.target.value)} />}</Field>
      {valid ? <p className="qop-total" dir="ltr">{money(locale, opp.currency, offered)} · {pct(bps)}</p> : null}
      {valid ? <ApprovalHint discountBps={bps} me={me} /> : null}
      <Field label={t('opportunities.admin.messageToCustomer')}>{(p) => <Textarea id={p.id} rows={3} maxLength={2000} value={message} onChange={(e) => setMessage(e.target.value)} />}</Field>
      <FormError error={error} />
      <DialogActions cancelLabel={t('opportunities.cancel')} onCancel={onCancel} submitLabel={t('opportunities.admin.sendRevision')} busy={busy} disabled={!valid} />
    </form>
  );
}

type Row = QuotationItemInput & { price: string };
function QuotationForm({ opp, from, me, busy, error, onSend, onCancel }: { opp: OpportunityView; from?: VersionView; me: Me | null; busy: boolean; error: string | null; onSend: (b: Record<string, unknown>) => Promise<void>; onCancel: () => void }) {
  const { t, locale } = useOpp();
  const seed = useMemo<Row[]>(() => from
    ? from.items.map((i) => ({ kind: i.kind, code: i.code, name: i.name, description: i.description, listPriceMinor: i.listPriceMinor, required: i.required, price: String(i.listPriceMinor / 100) }))
    : [
      ...opp.requirements.modules.map((m) => ({ kind: 'module' as const, code: m, name: t(`opportunities.module.${m}`), description: '', listPriceMinor: 0, required: false, price: '' })),
      ...opp.requirements.services.map((s) => ({ kind: 'service' as const, code: s, name: t(`opportunities.service.${s}`), description: '', listPriceMinor: 0, required: false, price: '' })),
    ], [from, opp.requirements]);
  const [rows, setRows] = useState<Row[]>(seed);
  const [discount, setDiscount] = useState('');
  const [message, setMessage] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const set = (i: number, patch: Partial<Row>) => setRows((rs) => rs.map((r, n) => (n === i ? { ...r, ...patch } : r)));
  const items = rows.map((r) => ({ ...r, listPriceMinor: toMinor(r.price) ?? -1 }));
  const valid = items.length > 0 && items.every((i) => i.name.trim() && i.listPriceMinor >= 0);
  const list = valid ? items.reduce((s, i) => s + i.listPriceMinor, 0) : 0;
  const bps = discount.trim() ? Math.round(Number(discount) * 100) : 0;
  const okBps = Number.isFinite(bps) && bps >= 0 && bps <= 10_000;

  return (
    <form className="qop-dform" onSubmit={(e) => {
      e.preventDefault();
      if (!valid || !okBps) return;
      void onSend({ items: items.map(({ price: _p, ...i }) => ({ ...i, name: i.name.trim(), code: i.code.trim() || 'custom' })), ...(bps ? { discountBps: bps } : {}), message, ...(validUntil ? { validUntil } : {}) });
    }}>
      <ul className="qop-qitems">
        {rows.map((r, i) => (
          <li key={i} className="qop-qitem">
            <Field label={t('opportunities.admin.item')}>{(p) => <Input id={p.id} maxLength={200} value={r.name} onChange={(e) => set(i, { name: e.target.value })} />}</Field>
            <div className="qop-pair">
              <Field label={t('opportunities.admin.kind')}>{(p) => <Select id={p.id} value={r.kind} onChange={(e) => set(i, { kind: e.target.value as Row['kind'] })}><option value="service">{t('opportunities.kind.service')}</option><option value="module">{t('opportunities.kind.module')}</option></Select>}</Field>
              <Field label={t('opportunities.admin.listPrice', { currency: opp.currency })}>{(p) => <Input id={p.id} inputMode="decimal" dir="ltr" value={r.price} onChange={(e) => set(i, { price: e.target.value })} />}</Field>
            </div>
            <Field label={t('opportunities.admin.itemDescription')}>{(p) => <Input id={p.id} maxLength={1000} value={r.description} onChange={(e) => set(i, { description: e.target.value })} />}</Field>
            <div className="qop-qitem__foot">
              <label className="qop-check"><input type="checkbox" checked={r.required} onChange={(e) => set(i, { required: e.target.checked })} /> {t('opportunities.required')}</label>
              <Button variant="ghost" size="sm" onClick={() => setRows((rs) => rs.filter((_, n) => n !== i))}>{t('opportunities.admin.remove')}</Button>
            </div>
          </li>
        ))}
      </ul>
      <div><Button variant="ghost" iconStart={<IconPlus />} onClick={() => setRows((rs) => [...rs, { kind: 'service', code: 'custom', name: '', description: '', listPriceMinor: 0, required: false, price: '' }])}>{t('opportunities.admin.addItem')}</Button></div>
      <div className="qop-pair">
        <Field label={t('opportunities.admin.byPercent')}>{(p) => <Input id={p.id} inputMode="decimal" dir="ltr" value={discount} onChange={(e) => setDiscount(e.target.value)} />}</Field>
        <Field label={t('opportunities.admin.validUntil')}>{(p) => <Input id={p.id} type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />}</Field>
      </div>
      {valid && okBps ? <p className="qop-total" dir="ltr">{money(locale, opp.currency, list)}{bps ? ` → ${money(locale, opp.currency, discounted(list, bps))} (${pct(bps)})` : ''}</p> : null}
      {valid && okBps && bps ? <ApprovalHint discountBps={bps} me={me} /> : null}
      <Field label={t('opportunities.admin.messageToCustomer')}>{(p) => <Textarea id={p.id} rows={3} maxLength={2000} value={message} onChange={(e) => setMessage(e.target.value)} />}</Field>
      <FormError error={error} />
      <DialogActions cancelLabel={t('opportunities.cancel')} onCancel={onCancel} submitLabel={t('opportunities.admin.sendQuotation')} busy={busy} disabled={!valid || !okBps} />
    </form>
  );
}

function CloseForm({ busy, error, onClose, onCancel }: { busy: boolean; error: string | null; onClose: (reason: string) => Promise<void>; onCancel: () => void }) {
  const { t } = useOpp();
  const [reason, setReason] = useState('');
  return (
    <form className="qop-dform" onSubmit={(e) => { e.preventDefault(); if (reason.trim().length >= 3) void onClose(reason.trim()); }}>
      <Field label={t('opportunities.admin.closeReason')} required>{(p) => <Textarea id={p.id} required rows={3} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />}</Field>
      <FormError error={error} />
      <DialogActions cancelLabel={t('opportunities.cancel')} onCancel={onCancel} submitLabel={t('opportunities.admin.closeLost')} busy={busy} disabled={reason.trim().length < 3} danger />
    </form>
  );
}

// ====================================================================== sales staff
type StaffForm = { userId: string; name: string; email: string; managerId: string; headOfOperations: boolean; limit: string; active: boolean };
const BLANK: StaffForm = { userId: '', name: '', email: '', managerId: '', headOfOperations: false, limit: '10', active: true };

function StaffFormBody({ initial, rows, busy, error, onSave, onCancel }: { initial: StaffForm; rows: StaffProfile[]; busy: boolean; error: string | null; onSave: (f: StaffForm) => void; onCancel: () => void }) {
  const { t } = useOpp();
  const [form, setForm] = useState(initial);
  return (
    <form className="qop-dform" onSubmit={(e) => { e.preventDefault(); onSave(form); }}>
      <Field label={t('opportunities.admin.staff.userId')} required>{(p) => <Input id={p.id} required dir="ltr" pattern="[A-Za-z0-9_-]{1,64}" value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })} />}</Field>
      <Field label={t('opportunities.admin.staff.name')} required>{(p) => <Input id={p.id} required maxLength={200} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />}</Field>
      <Field label={t('opportunities.admin.staff.email')} required>{(p) => <Input id={p.id} required type="email" dir="ltr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />}</Field>
      <Field label={t('opportunities.admin.staff.manager')}>{(p) => (
        <Select id={p.id} value={form.managerId} onChange={(e) => setForm({ ...form, managerId: e.target.value })}>
          <option value="">—</option>
          {rows.filter((s) => s.userId !== form.userId).map((s) => <option key={s.userId} value={s.userId}>{s.name}</option>)}
        </Select>
      )}</Field>
      <Field label={t('opportunities.admin.staff.limitPercent')} required>{(p) => <Input id={p.id} required inputMode="decimal" dir="ltr" value={form.limit} onChange={(e) => setForm({ ...form, limit: e.target.value })} />}</Field>
      <label className="qop-check"><input type="checkbox" checked={form.headOfOperations} onChange={(e) => setForm({ ...form, headOfOperations: e.target.checked })} /> {t('opportunities.admin.staff.hooCheck')}</label>
      <label className="qop-check"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> {t('opportunities.admin.staff.active')}</label>
      <p className="qop-muted">{t('opportunities.admin.staff.rule')}</p>
      <FormError error={error} />
      <DialogActions cancelLabel={t('opportunities.cancel')} onCancel={onCancel} submitLabel={t('opportunities.admin.staff.save')} busy={busy} />
    </form>
  );
}

export function SalesStaffScreen() {
  const { t, locale, roles, links, Screen, can, get, put } = useDesk();
  const copy = useCopy();
  const [rows, setRows] = useState<StaffProfile[] | null>(null);
  const [editing, setEditing] = useState<StaffForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const err = useErr();
  const load = () => get<StaffProfile[]>('/staff').then(setRows, (e: unknown) => setError(err(e)));
  useEffect(() => { void load(); }, []);
  const admin = can.manage(roles);
  const name = (uid: string | null) => (uid ? rows?.find((s) => s.userId === uid)?.name ?? uid : '—');

  const save = async (form: StaffForm) => {
    setBusy(true); setError(null); setNotice(null);
    try {
      await put(`/staff/${encodeURIComponent(form.userId.trim())}`, {
        name: form.name.trim(), email: form.email.trim(), managerId: form.managerId || null, headOfOperations: form.headOfOperations,
        selfApprovalBps: Math.round(Number(form.limit) * 100), active: form.active,
      });
      setNotice(t('opportunities.admin.staff.saved')); setEditing(null); await load();
    } catch (x) { setError(err(x)); } finally { setBusy(false); }
  };
  const editingExisting = !!editing && !!rows?.some((s) => s.userId === editing.userId);

  return (
    <KitProvider locale={locale}>
      <Screen title={copy.staff('title')} subtitle={copy.staff('intro')} testId="sales-staff"
        actions={admin ? <Button variant="accent" iconStart={<IconPlus />} onClick={() => { setError(null); setEditing(BLANK); }}>{t('opportunities.admin.staff.add')}</Button> : null}>
        <div className="qop">
          <BackLink to={links.list}>{t('opportunities.back')}</BackLink>
          {error && !editing ? <p role="alert" className="qop-error">{error}</p> : null}
          {notice ? <p role="status" className="qop-msg">{notice}</p> : null}
          {rows ? (
            <div className="qop-list">
              <div className="qop-rowhead qop-srow" aria-hidden="true">
                <span className="qop-srow__name">{t('opportunities.admin.staff.name')}</span><span className="qop-srow__mgr">{t('opportunities.admin.staff.manager')}</span><span className="qop-srow__limit">{t('opportunities.admin.staff.limit')}</span><span className="qop-srow__role">{t('opportunities.admin.staff.role')}</span><span />
              </div>
              <ul className="qop-rows">
                {rows.map((s) => (
                  <li key={s.userId} className="qop-srow qop-srow--item">
                    <span className="qop-srow__name"><span className="qop-pickrow__name">{s.name}{s.active ? '' : ` (${t('opportunities.admin.staff.inactive')})`}</span><span className="qop-pickrow__meta" dir="ltr">{s.email}</span></span>
                    <span className="qop-srow__mgr">{name(s.managerId)}</span>
                    <span className="qop-srow__limit" dir="ltr">{pct(s.selfApprovalBps)}</span>
                    <span className="qop-srow__role">{s.headOfOperations ? <Badge tone="info">{t('opportunities.admin.staff.hoo')}</Badge> : null}</span>
                    <span className="qop-srow__act">{admin ? <Button variant="ghost" size="sm" onClick={() => { setError(null); setEditing({ userId: s.userId, name: s.name, email: s.email, managerId: s.managerId ?? '', headOfOperations: s.headOfOperations, limit: String(s.selfApprovalBps / 100), active: s.active }); }}>{t('opportunities.admin.staff.edit')}</Button> : null}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <p className="qop-muted">{t('opportunities.admin.staff.rule')}</p>
        </div>
        <Dialog open={!!editing} onClose={() => setEditing(null)} title={editingExisting ? t('opportunities.admin.staff.edit') : t('opportunities.admin.staff.add')}>
          {editing ? <StaffFormBody initial={editing} rows={rows ?? []} busy={busy} error={error} onCancel={() => setEditing(null)} onSave={(f) => void save(f)} /> : null}
        </Dialog>
      </Screen>
    </KitProvider>
  );
}
